// Converts a list of available shift times to absolute minutes from midnight.
// Accepts either an array of 'HH:MM' strings or objects with a 'startTime' field.
function mapShiftTimesToMinutes(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const firstIsString = typeof list[0] === 'string';
  const times = firstIsString ? list.map(t => String(t)) : list.map(t => t && t.startTime);
  return times
    .filter(Boolean)
    .map(t => {
      const [hh, mm] = String(t).split(':').map(Number);
      return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
    })
    .filter(v => v !== null);
}

// Picks the candidate minute closest to the target minute.
function pickClosestMinute(candidates, targetMinutes) {
  if (!Array.isArray(candidates) || candidates.length === 0) return targetMinutes;
  let best = candidates[0];
  let bestDiff = Math.abs(best - targetMinutes);
  for (let i = 1; i < candidates.length; i++) {
    const c = candidates[i];
    const d = Math.abs(c - targetMinutes);
    if (d < bestDiff) {
      best = c;
      bestDiff = d;
    }
  }
  return best;
}

// Snaps a pixel position on the grid to the nearest available shift time or falls back to a fixed window distance.
// Returns both snapped absolute minutes and the snapped left pixel position.
export function snapToShiftTime({
  pxPositionLeft,
  hourWidthPx,
  gridOriginPx,
  availableShiftTimes,
  bookingWindowDistance = 15,
  dayStartAbsMinutes = 9 * 60,
}) {
  const pxPerMinute = hourWidthPx / 60;
  const totalMinutesFromStart = (pxPositionLeft - gridOriginPx) / pxPerMinute;
  const currentAbsMinutes = totalMinutesFromStart + dayStartAbsMinutes;

  const candidates = mapShiftTimesToMinutes(availableShiftTimes);

  let snappedAbsMinutes;
  if (candidates.length > 0) {
    snappedAbsMinutes = pickClosestMinute(candidates, currentAbsMinutes);
  } else {
    const snappedFromStart =
      Math.round(totalMinutesFromStart / bookingWindowDistance) * bookingWindowDistance;
    snappedAbsMinutes = snappedFromStart + dayStartAbsMinutes;
  }

  const snappedLeft = (snappedAbsMinutes - dayStartAbsMinutes) * pxPerMinute + gridOriginPx;
  return { snappedAbsMinutes, snappedLeft };
}

// Computes a preview start Date from a snapped left pixel position on the booking grid.
export function computePreviewStartFromLeft({
  snappedLeft,
  hourWidthPx,
  gridOriginPx,
  eventStartDate,
  dayStartAbsMinutes = 9 * 60,
}) {
  const minutesPerPixel = 60 / hourWidthPx;
  const minutesFromGridStart = (snappedLeft - gridOriginPx) * minutesPerPixel;
  const hourOffset = Math.floor(minutesFromGridStart / 60);
  const minuteInHour = Math.round(minutesFromGridStart % 60);
  const baseHour = Math.floor(dayStartAbsMinutes / 60);

  const previewHour = baseHour + hourOffset;
  const previewMinute = minuteInHour;

  const previewStart = new Date(eventStartDate.getTime());
  previewStart.setHours(previewHour, previewMinute, 0, 0);
  return previewStart;
}

function normalizeToIntervalStart(date, intervalMs) {
  if (!(date instanceof Date) || !Number.isFinite(intervalMs) || intervalMs <= 0) {
    return new Date(date);
  }
  const normalized = Math.floor(date.getTime() / intervalMs) * intervalMs;
  return new Date(normalized);
}

export function computeActiveLaneIndices(bookings, config) {
  const indices = new Set();
  if (!config || !Array.isArray(bookings) || bookings.length === 0) {
    return indices;
  }

  const { laneIndexById, laneCountById, intervalMs, isIntervalMode, order } = config;

  bookings.forEach(booking => {
    if (!booking) return;

    if (booking.status === 'blocked') {
      const ridRaw = booking?.resourceId?._id || booking?.resourceId;
      if (ridRaw) {
        const rid = String(ridRaw);
        const baseLane = laneIndexById.get(rid);
        const laneCount = laneCountById.get(rid) || 1;
        if (baseLane != null) {
          for (let offset = 0; offset < laneCount; offset += 1) {
            indices.add(baseLane + offset);
          }
        }
        return;
      }
      order.forEach(resId => {
        const baseLane = laneIndexById.get(resId);
        const laneCount = laneCountById.get(resId) || 1;
        if (baseLane != null) {
          for (let offset = 0; offset < laneCount; offset += 1) {
            indices.add(baseLane + offset);
          }
        }
      });
      return;
    }

    const orderedResourceIds = Array.isArray(booking.orderedResourceIds)
      ? booking.orderedResourceIds
      : [];
    if (orderedResourceIds.length === 0) {
      return;
    }

    const baseStartDate = new Date(booking.startTime);
    if (Number.isNaN(baseStartDate.getTime())) {
      return;
    }

    orderedResourceIds.forEach(segment => {
      const rid = segment?.resourceId?._id || segment?.resourceId;
      if (!rid) return;
      const ridStr = String(rid);
      const baseLane = laneIndexById.get(ridStr);
      if (baseLane == null) return;

      const laneCount = laneCountById.get(ridStr) || 1;
      const startOffsetMinutes = Number(segment?.startOffsetMinutes || 0);
      const segStartDate = new Date(baseStartDate.getTime() + startOffsetMinutes * 60000);
      const groupIndex = Number(segment?.groupIndex || 1);

      let laneIndex = baseLane;
      if (isIntervalMode && laneCount > 1 && Number.isFinite(intervalMs) && intervalMs > 0) {
        const slotStart = normalizeToIntervalStart(segStartDate, intervalMs);
        const slotIndex = Math.floor(slotStart.getTime() / intervalMs);
        const offset = ((slotIndex % laneCount) + laneCount) % laneCount;
        laneIndex = baseLane + offset;
      } else if (isIntervalMode && laneCount > 1) {
        const offset = (((groupIndex - 1) % laneCount) + laneCount) % laneCount;
        laneIndex = baseLane + offset;
      }
      indices.add(laneIndex);
    });
  });

  return indices;
}

export function computeBookingDurationMs(booking, orderedResourcesConfig) {
  if (!booking) return 0;

  const fallbackDuration = (() => {
    if (Number.isFinite(booking?.eventDurationMS) && booking.eventDurationMS > 0) {
      return booking.eventDurationMS;
    }
    const start = booking?.startTime ? new Date(booking.startTime).getTime() : NaN;
    const end = booking?.endTime ? new Date(booking.endTime).getTime() : NaN;
    return Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : 0;
  })();

  const entries = Array.isArray(booking?.orderedResourceIds) ? booking.orderedResourceIds : [];
  if (entries.length === 0) return fallbackDuration;

  const baseStartMs = booking?.startTime ? new Date(booking.startTime).getTime() : NaN;
  if (!Number.isFinite(baseStartMs)) return fallbackDuration;

  let minStart = baseStartMs;
  let maxEnd = baseStartMs;
  let hasMetadata = false;

  for (const entry of entries) {
    const offset = Number(entry?.startOffsetMinutes);
    const duration = Math.max(0, Number(entry?.durationMinutes));
    if (Number.isFinite(offset) && Number.isFinite(duration)) {
      hasMetadata = true;
      const segStart = baseStartMs + offset * 60000;
      const segEnd = segStart + duration * 60000;
      if (segStart < minStart) minStart = segStart;
      if (segEnd > maxEnd) maxEnd = segEnd;
    }
  }

  if (hasMetadata) {
    const metaDuration = Math.max(0, maxEnd - minStart);
    if (metaDuration > 0) return Math.max(metaDuration, fallbackDuration);
  }

  if (fallbackDuration > 0) return fallbackDuration;

  const durationMap = orderedResourcesConfig?.durationByIdMs;
  if (durationMap instanceof Map) {
    let sum = 0;
    for (const entry of entries) {
      const rid = entry?.resourceId?._id || entry?.resourceId;
      const mapped = rid ? durationMap.get(String(rid)) || 0 : 0;
      if (!mapped) return 0;
      sum += mapped;
    }
    if (sum > 0) return sum;
  }

  return 0;
}
